/**
 * scene-setup.js
 * Khởi tạo Scene Three.js, Camera, Renderer, OrbitControls, Ánh sáng và Bục Studio Pedestal.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { applyLightingPreset, fitShadowCamera } from './lighting-presets.js';
import { setupStudioStage } from './studio-stage.js';
import { syncWalkerCameraOnResize } from './modes/walker-controller.js';

export function initScene(container, state) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0f172a);
  scene.fog = new THREE.FogExp2(0x0f172a, 0.025);

  const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 150);
  camera.position.set(0, 1.8, 4.8);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.shadowMap.needsUpdate = true;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // Tạo Env Map từ RoomEnvironment qua PMREMGenerator cho chế độ Studio
  const pmremGenerator = new THREE.PMREMGenerator(renderer);
  pmremGenerator.compileEquirectangularShader();
  const roomEnv = new RoomEnvironment();
  const studioEnvMap = pmremGenerator.fromScene(roomEnv).texture;
  state.studioEnvMap = studioEnvMap;
  scene.environment = studioEnvMap;
  if ('environmentIntensity' in scene) {
    scene.environmentIntensity = 0.5;
  }
  pmremGenerator.dispose();
  roomEnv.dispose();

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.target.set(0, 1.1, 0);
  controls.maxPolarAngle = Math.PI / 2 + 0.05;

  const clock = new THREE.Clock();

  // --- LIGHTING ---
  const hemiLight = new THREE.HemisphereLight(0xe0f2fe, 0x64748b, 0.65);
  scene.add(hemiLight);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.38);
  scene.add(ambientLight);

  const dirLight = new THREE.DirectionalLight(0xfffbeb, 2.1);
  dirLight.position.set(-12, 16, 4);
  dirLight.castShadow = true;
  dirLight.shadow.mapSize.width = 2048;
  dirLight.shadow.mapSize.height = 2048;
  dirLight.shadow.normalBias = 0.02;
  dirLight.shadow.bias = -0.0005;
  scene.add(dirLight);
  scene.add(dirLight.target);

  const rimLight = new THREE.DirectionalLight(0x38bdf8, 0.8);
  rimLight.position.set(-6, 4, -6);
  scene.add(rimLight);

  const fillLight = new THREE.DirectionalLight(0xfffbeb, 0.55);
  fillLight.position.set(0, 3, 8);
  scene.add(fillLight);

  // --- SÂN KHẤU STUDIO 360° (CYCLORAMA, BỤC GỖ SỒI, SHADOW MATERIAL, GRID HELPER) ---
  setupStudioStage(scene, state);

  // Ghi vào state
  state.scene = scene;
  state.camera = camera;
  state.renderer = renderer;
  state.controls = controls;
  state.clock = clock;
  state.hemiLight = hemiLight;
  state.ambientLight = ambientLight;
  state.dirLight = dirLight;
  state.rimLight = rimLight;
  state.fillLight = fillLight;

  fitShadowCamera(state, { center: [0, 1, 0], halfSize: 5 });

  return { scene, camera, renderer, controls, clock };
}

export function setTimeOfDay(time, state) {
  if (!state.currentProject.environment) state.currentProject.environment = {};
  state.currentProject.environment.timeOfDay = time;

  const group = document.getElementById('group-time-of-day');
  if (group) {
    group.querySelectorAll('.chip-btn').forEach((b) => {
      b.classList.toggle('active', b.dataset.time === time);
    });
  }

  applyLightingPreset(state, time);
}

export function updateViewOffset(state) {
  if (!state.camera) return;

  const panel = document.querySelector('.control-panel');
  const isDesktop = window.innerWidth >= 900;
  const isPanelVisible =
    panel &&
    window.getComputedStyle(panel).display !== 'none' &&
    !panel.classList.contains('mobile-open') &&
    state.currentMode !== 'drama';

  if (isDesktop && isPanelVisible) {
    const rect = panel.getBoundingClientRect();
    const panelW = rect.width;
    if (panelW > 0) {
      state.camera.setViewOffset(
        window.innerWidth,
        window.innerHeight,
        panelW / 2,
        0,
        window.innerWidth,
        window.innerHeight
      );
    } else {
      state.camera.clearViewOffset();
    }
  } else {
    state.camera.clearViewOffset();
  }
}

export function handleResize(state) {
  if (!state.camera || !state.renderer) return;
  syncWalkerCameraOnResize(state);
  state.camera.aspect = window.innerWidth / window.innerHeight;
  state.camera.updateProjectionMatrix();
  state.renderer.setSize(window.innerWidth, window.innerHeight);
  updateViewOffset(state);
}
